<template>
  <VSnackbar
    v-model="showNotification"
    :timeout="10000"
    color="success"
    location="top right"
    multi-line
    vertical
  >
    <div class="d-flex flex-column">
      <div class="d-flex align-center mb-2">
        <VIcon class="mr-2" size="large">mdi-bell-ring</VIcon>
        <div class="d-flex align-center justify-space-between flex-grow-1">
          <span class="text-h6 font-weight-bold">New Order Received!</span>
          <VChip v-if="notificationQueue.length > 1" color="white" size="x-small" variant="outlined">
            +{{ notificationQueue.length - 1 }} more
          </VChip>
        </div>
      </div>
      <div v-if="currentNotification">
        <div class="mb-1">
          <strong>Order #{{ currentNotification.displayOrderId }}</strong>
        </div>
        <div class="text-body-2">
          <div>Customer: {{ currentNotification.data?.customerFirstName }} {{ currentNotification.data?.customerLastName }}</div>
          <div>Type: {{ currentNotification.data?.orderType?.toUpperCase() }}</div>
          <div v-if="currentNotification.data?.totalAmount">
            Amount: {{ formatCurrency(currentNotification.data.totalAmount) }}
          </div>
        </div>
      </div>
    </div>

    <template #actions>
      <VBtn
        color="white"
        variant="text"
        @click="viewOrder"
      >
        View
      </VBtn>
      <VBtn
        color="white"
        variant="text"
        @click="dismissNotification"
      >
        {{ notificationQueue.length > 1 ? 'Next' : 'Dismiss' }}
      </VBtn>
    </template>
  </VSnackbar>
</template>

<script setup>
import {computed, ref, watch} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import {useOrderEvents} from '~/composables/useOrderEvents'

const orderEvents = useOrderEvents()
const {incomingOrders, stopNotificationSound} = orderEvents || {}
const router = useRouter()
const route = useRoute()

const showNotification = ref(false)
const notificationQueue = ref([])
const processedOrderIds = new Set()

const currentNotification = computed(() => notificationQueue.value[0] || null)

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00'
  return `$${Number(value).toFixed(2)}`
}

const viewOrder = () => {
  stopNotificationSound()
  showNotification.value = false
  notificationQueue.value = []
  if (route.path !== '/orders') {
    router.push('/orders')
  }
}

const dismissNotification = () => {
  stopNotificationSound()

  notificationQueue.value.shift()

  if (notificationQueue.value.length === 0) {
    showNotification.value = false
  } else {
    showNotification.value = true
  }
}

if (incomingOrders) {
  watch(incomingOrders, (orders) => {
    if (!orders || orders.length === 0) return

    const newOrders = orders.filter(order =>
      order.type === 'new_order' &&
      !processedOrderIds.has(order.orderId)
    )

    if (newOrders.length > 0) {
      newOrders.forEach(newOrder => {
        notificationQueue.value.push(newOrder)
        processedOrderIds.add(newOrder.orderId)
      })

      showNotification.value = true

      if (processedOrderIds.size > 100) {
        const idsArray = Array.from(processedOrderIds)
        processedOrderIds.clear()
        idsArray.slice(-50).forEach(id => processedOrderIds.add(id))
      }
    }
  }, {deep: true})
}

watch(showNotification, (isShowing) => {
  if (!isShowing && notificationQueue.value.length > 0) {
    notificationQueue.value = []
  }
})
</script>

<style scoped>
:deep(.v-snackbar__content) {
  padding: 16px;
}
</style>
